/**
 * Store adhésion isolé (localStorage dédié) — DEMO et robot séparés, nettoyable.
 */
import {
  MEMBERSHIP_STORAGE_KEY,
  ROBOT_ORIGIN,
  SANDBOX_MEMBER_PASSWORD,
  MEMBERSHIP_STATUSES,
  PAYMENT_STATUSES,
  REGISTRATION_KIND,
  BLACK_DRAGON_STATUSES,
} from "./membershipConstants.js";
import {
  defaultMembershipSettings,
  buildDemoMembershipSeed,
  buildDemoCredentialsSeed,
  buildDemoBlackDragonContent,
} from "./membershipDemoSeed.js";
import {
  activateMembership,
  canActivateMembership,
  deriveMembershipStatus,
  emptyConsents,
  emptyEmergency,
  emptyIdentity,
  emptyParent,
  evaluateMembershipCompleteness,
} from "./membershipEngine.js";
import { emptyHealthAnswers, getHealthFormVersion, isHealthFormComplete } from "./healthFormCatalog.js";
import { getPaymentProvider } from "./paymentProvider.js";
import { decideBlackDragon, submitBlackDragonIntent } from "./blackDragonEngine.js";
import { makeHealthAuditEntry } from "./healthPermissions.js";
import { addItem, getDataStore, getProgressionByStudentId } from "@/data/dataStore.js";

function cloneInitial() {
  return {
    settings: defaultMembershipSettings(),
    memberships: buildDemoMembershipSeed(),
    credentials: buildDemoCredentialsSeed(),
    blackDragonContent: buildDemoBlackDragonContent(),
    healthAudit: [],
  };
}

function loadState() {
  if (typeof localStorage === "undefined") return cloneInitial();
  try {
    const raw = localStorage.getItem(MEMBERSHIP_STORAGE_KEY);
    if (!raw) return cloneInitial();
    const parsed = JSON.parse(raw);
    return {
      settings: { ...defaultMembershipSettings(), ...(parsed.settings || {}) },
      memberships: Array.isArray(parsed.memberships) ? parsed.memberships : cloneInitial().memberships,
      credentials: Array.isArray(parsed.credentials) ? parsed.credentials : cloneInitial().credentials,
      blackDragonContent: Array.isArray(parsed.blackDragonContent)
        ? parsed.blackDragonContent
        : cloneInitial().blackDragonContent,
      healthAudit: Array.isArray(parsed.healthAudit) ? parsed.healthAudit : [],
    };
  } catch {
    return cloneInitial();
  }
}

let state = loadState();

function persist() {
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(MEMBERSHIP_STORAGE_KEY, JSON.stringify(state));
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("karate-membership-updated"));
  }
}

export function getMembershipStore() {
  return state;
}

export function resetMembershipStore() {
  state = cloneInitial();
  persist();
  return state;
}

export function purgeRobotMembershipData() {
  const isRobotMembership = (m) =>
    m.origin === ROBOT_ORIGIN ||
    String(m.identity?.email || "").includes("shotokan-robot.test") ||
    String(m.parent?.email || "").includes("shotokan-robot.test");
  const isRobotCred = (c) =>
    c.origin === ROBOT_ORIGIN || String(c.email || "").includes("shotokan-robot.test");
  state = {
    ...state,
    memberships: state.memberships.filter((m) => !isRobotMembership(m)),
    credentials: state.credentials.filter((c) => !isRobotCred(c)),
    healthAudit: state.healthAudit.filter((a) => !String(a.membershipId || "").includes("robot")),
  };
  persist();
  return state;
}

export function updateMembershipSettings(patch) {
  state = { ...state, settings: { ...state.settings, ...patch } };
  persist();
  return state.settings;
}

function newId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function createMembershipDraft({ kind = REGISTRATION_KIND.ADULT, origin = "user", demo = true } = {}) {
  const now = new Date().toISOString();
  const record = {
    id: newId(origin === ROBOT_ORIGIN ? "ms-robot" : "ms"),
    kind,
    status: MEMBERSHIP_STATUSES.DRAFT,
    demo,
    origin,
    studentId: null,
    identity: emptyIdentity(),
    emergency: emptyEmergency(),
    parent: kind === REGISTRATION_KIND.MINOR ? emptyParent() : null,
    health: {
      version: getHealthFormVersion(kind),
      completedAt: null,
      completedBy: null,
      answers: emptyHealthAnswers(),
    },
    consents: emptyConsents(kind),
    payment: {
      status: PAYMENT_STATUSES.NONE,
      amountCents: state.settings.annualFeeCents,
      currency: state.settings.currency,
      testMode: true,
    },
    blackDragon: { status: BLACK_DRAGON_STATUSES.NOT_MEMBER, history: [] },
    memberPermissionsGranted: false,
    adminPermissionsGranted: false,
    createdAt: now,
    updatedAt: now,
  };
  state = { ...state, memberships: [record, ...state.memberships] };
  persist();
  return record;
}

export function getMembershipById(id) {
  return state.memberships.find((m) => m.id === id) || null;
}

export function findMembershipsByEmail(email) {
  const key = String(email || "").toLowerCase();
  return state.memberships.filter(
    (m) =>
      String(m.identity?.email || "").toLowerCase() === key ||
      String(m.parent?.email || "").toLowerCase() === key,
  );
}

export function findMembershipForUsername(username) {
  const key = String(username || "").toLowerCase();
  const cred = state.credentials.find((c) => String(c.email).toLowerCase() === key);
  if (cred?.membershipId) {
    const byId = getMembershipById(cred.membershipId);
    if (byId) return { membership: byId, credential: cred };
  }
  const list = findMembershipsByEmail(key);
  const active = list.find((m) => m.status === MEMBERSHIP_STATUSES.ACTIVE) || list[0];
  return { membership: active || null, credential: cred || null };
}

export function patchMembership(id, patch) {
  const idx = state.memberships.findIndex((m) => m.id === id);
  if (idx < 0) return null;
  const nextList = [...state.memberships];
  const merged = { ...nextList[idx], ...patch, id, updatedAt: new Date().toISOString() };
  if (!TERMINAL_KEEP.has(merged.status)) {
    merged.status = deriveMembershipStatus(merged);
  }
  nextList[idx] = merged;
  state = { ...state, memberships: nextList };
  persist();
  return merged;
}

const TERMINAL_KEEP = new Set([
  MEMBERSHIP_STATUSES.ACTIVE,
  MEMBERSHIP_STATUSES.EXPIRED,
  MEMBERSHIP_STATUSES.SUSPENDED,
  MEMBERSHIP_STATUSES.CANCELLED,
  MEMBERSHIP_STATUSES.PENDING_APPROVAL,
]);

export function updateIdentity(id, identityPatch, extra = {}) {
  const current = getMembershipById(id);
  if (!current) return null;
  return patchMembership(id, {
    identity: { ...current.identity, ...identityPatch },
    emergency: extra.emergency ? { ...current.emergency, ...extra.emergency } : current.emergency,
    parent: extra.parent ? { ...(current.parent || emptyParent()), ...extra.parent } : current.parent,
  });
}

export function updateHealth(id, answers, completedBy) {
  const current = getMembershipById(id);
  if (!current) return null;
  const complete = isHealthFormComplete(answers, current.kind);
  return patchMembership(id, {
    health: {
      version: getHealthFormVersion(current.kind),
      answers,
      completedAt: complete ? new Date().toISOString() : null,
      completedBy: complete ? completedBy || answers.completedBy || current.identity?.email : current.health?.completedBy,
    },
  });
}

export function updateConsents(id, consentsPatch) {
  const current = getMembershipById(id);
  if (!current) return null;
  return patchMembership(id, { consents: { ...current.consents, ...consentsPatch } });
}

export async function chargeMembershipSandbox(id, method, payerEmail) {
  const current = getMembershipById(id);
  if (!current) return { ok: false, error: "not_found" };
  const completeness = evaluateMembershipCompleteness({
    ...current,
    payment: { ...current.payment, status: PAYMENT_STATUSES.CONFIRMED },
  });
  if (completeness.missing.filter((m) => m !== "payment").length) {
    return { ok: false, error: "incomplete", missing: completeness.missing };
  }
  const provider = getPaymentProvider();
  const result = await provider.charge({
    amountCents: state.settings.annualFeeCents,
    currency: state.settings.currency,
    method,
    membershipId: id,
    payerEmail,
  });
  if (!result.ok) {
    patchMembership(id, {
      payment: { ...current.payment, status: PAYMENT_STATUSES.FAILED, error: result.error, testMode: true },
    });
    return { ok: false, error: result.error, result };
  }
  const withPay = patchMembership(id, {
    payment: {
      status: PAYMENT_STATUSES.CONFIRMED,
      amountCents: result.amountCents,
      currency: result.currency,
      method: result.method,
      provider: result.provider,
      transactionId: result.transactionId,
      paidAt: result.paidAt,
      testMode: true,
    },
  });
  return activateAndProvision(withPay);
}

function activateAndProvision(record) {
  if (!record) return { ok: false, error: "not_found" };
  if (!canActivateMembership(record)) {
    return { ok: false, error: "not_ready", record, missing: evaluateMembershipCompleteness(record).missing };
  }
  const activated = activateMembership(record);
  if (!activated.ok) return { ok: false, error: "activate_failed", record: activated.record };
  const saved = patchMembership(record.id, {
    ...activated.record,
    status: MEMBERSHIP_STATUSES.ACTIVE,
  });
  provisionMemberAccount(saved);
  return { ok: true, record: saved };
}

function provisionMemberAccount(record) {
  const studentId = record.studentId || `ms-student-${record.id}`;
  const store = getDataStore();
  if (!store.etudiants.some((e) => e.id === studentId)) {
    try {
      addItem("etudiants", {
        id: studentId,
        prenom: record.identity.firstName,
        nom: record.identity.lastName,
        dojo_id: record.identity.dojoId,
        groupe: record.kind === REGISTRATION_KIND.MINOR ? "Enfants" : "Adultes",
        ceinture: "Blanche",
        statut: "actif",
        demo: Boolean(record.demo),
      });
    } catch {
      /* already exists */
    }
  }
  if (!getProgressionByStudentId(studentId)) {
    try {
      addItem("progressions", {
        id: `ms-prog-${record.id}`,
        student_id: studentId,
        ceinture_actuelle_id: "belt-white",
        prochaine_ceinture_id: "belt-yellow",
        pourcentage_progression: 0,
        statut: "en_progression",
        criteres: {},
        notes_sensei: "",
        notes_sempai: "",
        recommandation_examen: false,
        demo: Boolean(record.demo),
      });
    } catch {
      /* ignore */
    }
  }
  patchMembership(record.id, { studentId, adminPermissionsGranted: false, memberPermissionsGranted: true });

  if (record.kind === REGISTRATION_KIND.MINOR && record.parent?.email) {
    upsertCredential({
      email: record.parent.email,
      password: SANDBOX_MEMBER_PASSWORD,
      role: "parent",
      membershipId: record.id,
      linkedMembershipIds: [record.id],
      name: `${record.parent.firstName} ${record.parent.lastName}`.trim(),
      dojoId: record.identity.dojoId,
      studentId,
      origin: record.origin,
      demo: record.demo,
    });
  } else if (record.identity?.email) {
    upsertCredential({
      email: record.identity.email,
      password: SANDBOX_MEMBER_PASSWORD,
      role: "student",
      membershipId: record.id,
      linkedMembershipIds: [record.id],
      name: `${record.identity.firstName} ${record.identity.lastName}`.trim(),
      dojoId: record.identity.dojoId,
      studentId,
      origin: record.origin,
      demo: record.demo,
    });
  }
}

function upsertCredential(cred) {
  const key = String(cred.email).toLowerCase();
  const existing = state.credentials.find((c) => String(c.email).toLowerCase() === key);
  if (existing) {
    const linked = [...new Set([...(existing.linkedMembershipIds || []), ...(cred.linkedMembershipIds || [])])];
    state = {
      ...state,
      credentials: state.credentials.map((c) =>
        String(c.email).toLowerCase() === key ? { ...existing, ...cred, linkedMembershipIds: linked, password: existing.password || cred.password } : c,
      ),
    };
  } else {
    state = { ...state, credentials: [...state.credentials, cred] };
  }
  persist();
}

export function findMembershipCredential(identifier, password) {
  const key = String(identifier || "").trim().toLowerCase();
  const secret = String(password || "").trim();
  const cred = state.credentials.find((c) => String(c.email).toLowerCase() === key);
  if (!cred) return null;
  if (String(cred.password).toLowerCase() !== secret.toLowerCase()) return null;
  return cred;
}

export function appendHealthAudit(entry) {
  const row = entry.id ? entry : makeHealthAuditEntry(entry);
  state = { ...state, healthAudit: [row, ...state.healthAudit].slice(0, 500) };
  persist();
  return row;
}

export function submitBdRequest(membershipId, actor) {
  const current = getMembershipById(membershipId);
  if (!current) return { ok: false, code: "not_found" };
  const result = submitBlackDragonIntent(current, "request", actor);
  if (!result.ok) return result;
  return { ok: true, record: patchMembership(membershipId, { blackDragon: result.membership.blackDragon }) };
}

export function inviteBdMember(membershipId, actor) {
  const current = getMembershipById(membershipId);
  if (!current) return { ok: false, code: "not_found" };
  const result = submitBlackDragonIntent(current, "invite", actor);
  if (!result.ok) return result;
  return { ok: true, record: patchMembership(membershipId, { blackDragon: result.membership.blackDragon }) };
}

export function decideBdMember(membershipId, decision, actor) {
  const current = getMembershipById(membershipId);
  if (!current) return { ok: false, code: "not_found" };
  const result = decideBlackDragon(current, decision, actor);
  if (!result.ok) return result;
  return { ok: true, record: patchMembership(membershipId, { blackDragon: result.membership.blackDragon }) };
}

/** Remplace uniquement les adhésions d'origine CKA — aucun credential créé. */
export function replaceCkaImportedMemberships(imported) {
  const ORIGIN = "CKA_2025_26_IMPORT";
  const kept = state.memberships.filter((m) => m.origin !== ORIGIN);
  const map = new Map(kept.map((m) => [m.id, m]));
  for (const rec of imported) {
    const previous = map.get(rec.id);
    map.set(rec.id, previous ? { ...previous, ...rec, id: rec.id } : rec);
  }
  state = { ...state, memberships: [...map.values()] };
  persist();
  return state.memberships.filter((m) => m.origin === ORIGIN);
}

export function subscribeMembership(callback) {
  if (typeof window === "undefined") return () => {};
  window.addEventListener("karate-membership-updated", callback);
  return () => window.removeEventListener("karate-membership-updated", callback);
}
